import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Mail, 
  Lock, 
  User, 
  Phone, 
  ArrowRight, 
  CheckCircle2, 
  AlertCircle,
  Eye,
  EyeOff,
  ShieldCheck
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export default function Login() {
  const { t, lang } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        navigate('/portal');
      } else {
        await register(formData.name, formData.email, formData.password, formData.phone);
        alert(t('auth.regSuccessAlert') || 'Registrasi berhasil! Silakan login.');
        setIsLogin(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <style>{`
        .auth-page {
          min-height: 100vh;
          display: flex;
          background: #f5f5f1;
        }
        .auth-container {
          display: grid;
          grid-template-columns: 440px 1fr;
          width: 100%;
          min-height: 100vh;
        }

        /* ── Left brand panel ── */
        .auth-side {
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 48px 44px;
          background: #1e1e1c;
          color: #fff;
          overflow: hidden;
        }
        .auth-side::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(160deg, #2f2f28 0%, #1a1a16 60%, #0e0e0b 100%);
          z-index: 0;
        }
        .auth-side-content { position: relative; z-index: 1; }
        .auth-side-deco {
          position: absolute;
          bottom: -80px;
          right: -80px;
          width: 320px;
          height: 320px;
          border-radius: 50%;
          background: #bbd448;
          opacity: 0.07;
          z-index: 0;
        }
        .auth-side-deco2 {
          position: absolute;
          top: -60px;
          left: -60px;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: #777654;
          opacity: 0.12;
          z-index: 0;
        }
        .auth-logo {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 56px;
          text-decoration: none;
        }
        .auth-logo img {
          height: 44px;
          width: auto;
          filter: brightness(0) invert(1);
        }
        .auth-logo-mark {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border-radius: 8px;
          background: #bbd448;
          color: #1a1a16;
          font-size: 15px;
          font-weight: 900;
          letter-spacing: -0.5px;
        }
        .auth-logo span {
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: -0.3px;
        }
        .auth-side h1 {
          margin-bottom: 14px;
          color: #fff;
          font-size: 30px;
          font-weight: 800;
          line-height: 1.25;
          letter-spacing: -0.5px;
        }
        .auth-side > .auth-side-content > p {
          margin-bottom: 40px;
          color: rgba(255,255,255,0.65);
          font-size: 15px;
          line-height: 1.65;
        }
        .auth-features { display: flex; flex-direction: column; gap: 16px; }
        .auth-f-item {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .auth-f-ico {
          width: 32px;
          height: 32px;
          display: grid;
          place-items: center;
          border-radius: 6px;
          background: rgba(187,212,72,0.15);
          color: #bbd448;
          flex-shrink: 0;
        }
        .auth-f-item span {
          color: rgba(255,255,255,0.82);
          font-size: 14px;
          font-weight: 500;
        }
        .auth-side-bottom {
          position: relative;
          z-index: 1;
          padding-top: 32px;
          border-top: 1px solid rgba(255,255,255,0.1);
          color: rgba(255,255,255,0.4);
          font-size: 11px;
        }

        /* ── Right form panel ── */
        .auth-main {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 32px;
          background: #f5f5f1;
        }
        .auth-form-wrap {
          width: 100%;
          max-width: 420px;
        }
        .auth-mobile-logo {
          display: none;
          align-items: center;
          gap: 10px;
          margin-bottom: 32px;
          text-decoration: none;
        }
        .auth-header {
          margin-bottom: 32px;
        }
        .auth-header h2 {
          margin-bottom: 6px;
          color: #1a1a1a;
          font-size: 26px;
          font-weight: 800;
          letter-spacing: -0.4px;
        }
        .auth-header p {
          color: #666;
          font-size: 14px;
          line-height: 1.55;
        }

        /* Form */
        .auth-form { display: flex; flex-direction: column; gap: 18px; }
        .fg { display: flex; flex-direction: column; gap: 7px; }
        .fg label {
          color: #3a3a3a;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .label-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .label-row label {
          color: #3a3a3a;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .forgot-link {
          color: #777654;
          font-size: 12px;
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .fi-wrap {
          position: relative;
          display: flex;
          align-items: center;
          background: #fff;
          border: 1.5px solid #deded8;
          border-radius: 8px;
          transition: border-color 0.18s ease, box-shadow 0.18s ease;
        }
        .fi-wrap:focus-within {
          border-color: #777654;
          box-shadow: 0 0 0 3px rgba(119,118,84,0.12);
        }
        .fi-ico {
          flex-shrink: 0;
          margin-left: 14px;
          color: #aaa;
        }
        .fi-wrap input {
          flex: 1;
          height: 50px;
          padding: 0 14px;
          border: 0;
          outline: 0;
          background: transparent;
          color: #1a1a1a;
          font: 400 15px var(--font-main, inherit);
        }
        .fi-wrap input::placeholder { color: #bbb; }
        .fi-toggle {
          flex-shrink: 0;
          margin-right: 12px;
          color: #aaa;
        }
        .fi-toggle:hover { color: #777654; }

        /* Error */
        .auth-error {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 12px 14px;
          border-radius: 7px;
          background: #fff4f2;
          border: 1px solid #efb5ae;
          color: #b42318;
          font-size: 13px;
          font-weight: 500;
        }

        /* Submit button */
        .auth-submit {
          height: 52px;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.2px;
          background: #777654;
          border-color: #777654;
          margin-top: 4px;
          gap: 9px;
        }
        .auth-submit:hover { background: #65644a; border-color: #65644a; }
        .w-full { width: 100%; justify-content: center; }

        /* Switch */
        .auth-switch {
          text-align: center;
          color: #777;
          font-size: 13px;
        }
        .auth-switch button {
          color: #777654;
          font-size: 13px;
          font-weight: 700;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .auth-switch button:hover { color: #65644a; }

        /* Footer */
        .auth-footer { margin-top: 24px; }
        .auth-trust {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          color: #999;
          font-size: 12px;
        }
        .auth-trust svg { color: #777654; }

        /* Responsive */
        @media (max-width: 900px) {
          .auth-container { grid-template-columns: 1fr; }
          .auth-side { display: none; }
          .auth-main {
            min-height: 100vh;
            align-items: flex-start;
            padding: 36px 24px;
          }
          .auth-mobile-logo { display: flex; }
          .auth-form-wrap { max-width: 100%; }
        }
      `}</style>
      <div className="auth-container">
        {/* Left: Branding & Info */}
        <div className="auth-side">
          <div className="auth-side-deco" />
          <div className="auth-side-deco2" />
          <div className="auth-side-content">
            <Link to="/" className="auth-logo">
              <img src="/logo_sbb.png" alt="Solusi Bangun Beton" />
            </Link>
            <h1>{isLogin ? (lang === 'id' ? 'Selamat Datang Kembali' : 'Welcome Back') : (lang === 'id' ? 'Bergabung Bersama Kami' : 'Join Us')}</h1>
            <p>{isLogin ? t('auth.loginSub') : t('auth.regSub')}</p>
            <div className="auth-features">
              <div className="auth-f-item">
                <div className="auth-f-ico"><CheckCircle2 size={18} /></div>
                <span>{lang === 'id' ? 'Pantau pengiriman beton secara real-time' : 'Track concrete delivery in real-time'}</span>
              </div>
              <div className="auth-f-item">
                <div className="auth-f-ico"><CheckCircle2 size={18} /></div>
                <span>{lang === 'id' ? 'Invoice & dokumen digital otomatis' : 'Automatic digital invoices & documents'}</span>
              </div>
              <div className="auth-f-item">
                <div className="auth-f-ico"><CheckCircle2 size={18} /></div>
                <span>{lang === 'id' ? 'Dukungan teknis 24/7' : '24/7 technical support'}</span>
              </div>
            </div>
          </div>
          <div className="auth-side-bottom">
            © {new Date().getFullYear()} PT Solusi Bangun Beton · Member of SIG
          </div>
        </div>

        {/* Right: Form */}
        <div className="auth-main">
          <div className="auth-form-wrap">
            <Link to="/" className="auth-mobile-logo show-mobile">
              <div className="auth-logo-mark">SB</div>
              <span>Solusi Bangun Beton</span>
            </Link>
            
            <div className="auth-header">
              <h2>{isLogin ? t('auth.loginTitle') : t('auth.regTitle')}</h2>
              <p>{isLogin ? t('auth.loginSubShort') || t('auth.loginSub') : t('auth.regSubShort') || t('auth.regSub')}</p>
            </div>

            <form className="auth-form" onSubmit={handleSubmit}>
              {error && (
                <div className="auth-error">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
              {!isLogin && (
                <div className="fg">
                  <label>{t('auth.name')}</label>
                  <div className="fi-wrap">
                    <User className="fi-ico" size={18} />
                    <input 
                      type="text" 
                      placeholder="Contoh: Budi Wijaya" 
                      required 
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                </div>
              )}

              <div className="fg">
                <label>{t('auth.email')}</label>
                <div className="fi-wrap">
                  <Mail className="fi-ico" size={18} />
                  <input 
                    type="email" 
                    placeholder="nama@email.com" 
                    required 
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                  />
                </div>
              </div>

              {!isLogin && (
                <div className="fg">
                  <label>{t('auth.phone')}</label>
                  <div className="fi-wrap">
                    <Phone className="fi-ico" size={18} />
                    <input 
                      type="tel" 
                      placeholder="0812..." 
                      required 
                      value={formData.phone}
                      onChange={e => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                </div>
              )}

              <div className="fg">
                <div className="label-row">
                  <label>{t('auth.password')}</label>
                  {isLogin && <a href="#" className="forgot-link">{t('auth.forgot')}</a>}
                </div>
                <div className="fi-wrap">
                  <Lock className="fi-ico" size={18} />
                  <input 
                    type={showPass ? "text" : "password"} 
                    placeholder="••••••••" 
                    required 
                    value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}
                  />
                  <button type="button" className="fi-toggle" onClick={() => setShowPass(!showPass)}>
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button type="submit" className="btn btn-red w-full auth-submit">
                {isLogin ? t('auth.submit') : t('auth.regSubmit') || t('auth.submit')} <ArrowRight size={18} />
              </button>

              <div className="auth-switch">
                {isLogin ? t('auth.noAccount') : t('auth.haveAccount')} {' '}
                <button type="button" onClick={() => setIsLogin(!isLogin)}>
                  {isLogin ? t('auth.register') : t('auth.login')}
                </button>
              </div>
            </form>

            <div className="auth-footer">
              <div className="auth-trust">
                <ShieldCheck size={16} /> Data Anda aman & terenkripsi
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
